import { ReadersService } from "@/client"
import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { OrganizationContext, isAllOrgs } from "@/contexts/OrganizationContext"
import useAuth from "@/hooks/useAuth"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { Box, Flex, Grid, Heading, Spinner, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import { useContext } from "react"
import { useTranslation } from "react-i18next"
import { FaUsers } from "react-icons/fa"
import { FiActivity, FiCpu, FiWifi } from "react-icons/fi"

export const Route = createFileRoute("/_layout/")({
  component: Dashboard,
})

function Dashboard() {
  const sc = useSemanticColors()
  const { t } = useTranslation("dashboard")
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const { currentOrganization } = useContext(OrganizationContext)

  const { data: tagStats } = useQuery({
    queryKey: ["tags-dashboard-count", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) return { count: 0 }
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const url = isAllOrgs(currentOrganization)
        ? `${base}/api/v1/tags/paginated?skip=0&limit=1`
        : `${base}/api/v1/organizations/${currentOrganization.id}/tags/paginated?skip=0&limit=1`
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return { count: 0 }
      return res.json() as Promise<{ data: any[]; count: number }>
    },
    enabled: !!currentOrganization,
  })

  const { data: readers = [] } = useQuery({
    queryKey: ["readers-dashboard", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) return []
      try {
        if (isAllOrgs(currentOrganization)) {
          return await ReadersService.readReaders({ skip: 0, limit: 100 })
        }
        return await ReadersService.readOrganizationReaders({
          organisationId: currentOrganization.id,
          skip: 0,
          limit: 100,
        })
      } catch {
        return []
      }
    },
    enabled: !!currentOrganization,
  })

  const { data: recentActivity = [], isLoading: activityLoading } = useQuery({
    queryKey: ["recent-activity-dashboard", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization || isAllOrgs(currentOrganization)) return []
      try {
        const token = localStorage.getItem("access_token")
        const base = (OpenAPI.BASE || "").replace(/\/$/, "")
        const url = `${base}/api/v1/organizations/${currentOrganization.id}/tags/recent-activity?minutes=60&limit=10`
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return []
        return res.json()
      } catch {
        return []
      }
    },
    enabled: !!currentOrganization && !isAllOrgs(currentOrganization),
    refetchInterval: 30000,
  })

  const tagCount = tagStats?.count ?? 0
  const onlineReaders = readers.length

  const stats = [
    {
      icon: FiCpu,
      label: t("stats.totalTags"),
      value: tagCount,
      sub: t("stats.registered"),
      color: "brand.200",
    },
    {
      icon: FiWifi,
      label: t("stats.readers"),
      value: onlineReaders,
      sub: t("stats.configured"),
      color: "blue.400",
    },
    {
      icon: FiActivity,
      label: t("stats.recentDetections"),
      value: recentActivity.length,
      sub: t("stats.last60min"),
      color: "green.400",
    },
    {
      icon: FaUsers,
      label: t("stats.organization"),
      value: currentOrganization?.name ?? "—",
      sub: t("stats.active"),
      color: "purple.400",
    },
  ]

  return (
    <Box>
      <Box mb={{ base: 5, md: 8 }}>
        <Heading
          size={{ base: "lg", md: "xl" }}
          color={sc.heading}
          mb={2}
          lineClamp={2}
        >
          {t("welcome", { name: currentUser?.full_name || currentUser?.email })}
        </Heading>
        <Text color={sc.textMuted} fontSize={{ base: "sm", md: "md" }}>
          {currentOrganization
            ? t("overview", { orgName: currentOrganization.name })
            : t("selectOrg")}
        </Text>
      </Box>

      <Grid
        templateColumns={{
          base: "repeat(2, 1fr)",
          md: "repeat(2, 1fr)",
          lg: "repeat(4, 1fr)",
        }}
        gap={{ base: 3, md: 4, lg: 6 }}
        mb={{ base: 5, md: 8 }}
      >
        {stats.map((stat, index) => (
          <VisionCard
            key={index}
            variant="gradient"
            p={{ base: 3, md: "22px" }}
            minW={0}
          >
            <Flex justify="space-between" align="center" gap={2}>
              <Box minW={0} flex={1}>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  fontWeight="500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={2}
                  lineClamp={1}
                >
                  {stat.label}
                </Text>
                <Text
                  fontSize={{ base: "lg", md: "2xl" }}
                  fontWeight="700"
                  color={sc.heading}
                  lineClamp={1}
                >
                  {stat.value}
                </Text>
                <Text
                  fontSize={{ base: "xs", md: "sm" }}
                  color={sc.textMuted}
                  mt={1}
                  fontWeight="500"
                  lineClamp={1}
                >
                  {stat.sub}
                </Text>
              </Box>
              <IconBox
                variant="gradient"
                size="lg"
                display={{ base: "none", sm: "flex" }}
              >
                <stat.icon fontSize="28px" color={sc.iconColor} />
              </IconBox>
            </Flex>
          </VisionCard>
        ))}
      </Grid>

      <Grid
        templateColumns={{ base: "1fr", lg: "2fr 1fr" }}
        gap={{ base: 3, md: 4, lg: 6 }}
      >
        <VisionCard>
          <Heading size="md" color={sc.heading} mb={6} fontWeight="700">
            {t("recentDetections")}
          </Heading>
          {activityLoading ? (
            <Flex justify="center" py={8}>
              <Spinner color={sc.spinnerColor} />
            </Flex>
          ) : recentActivity.length === 0 ? (
            <Flex direction="column" align="center" py={8} gap={2}>
              <FiWifi fontSize="32px" color={sc.iconMuted} />
              <Text color={sc.textMuted} fontSize="sm">
                {t("noDetections")}
              </Text>
            </Flex>
          ) : (
            <Flex direction="column" gap={3}>
              {recentActivity.map((event: any, index: number) => (
                <Flex
                  key={index}
                  align="center"
                  gap={3}
                  p={2}
                  borderRadius="8px"
                  _hover={{ bg: sc.hoverBg }}
                >
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={sc.indicatorBg}
                    flexShrink={0}
                  />
                  <Box flex="1">
                    <Text color={sc.heading} fontSize="sm" fontWeight="500">
                      {event.tag_name || event.epcid}
                    </Text>
                    <Text color={sc.textSubtle} fontSize="xs">
                      EPC: {event.epcid}
                    </Text>
                  </Box>
                  {event.rssi != null && (
                    <Text color={sc.accentText} fontSize="xs" fontWeight="600">
                      {event.rssi} dBm
                    </Text>
                  )}
                  <Text color={sc.textSubtle} fontSize="xs" flexShrink={0}>
                    {event.timestamp
                      ? format(new Date(event.timestamp), "HH:mm:ss")
                      : ""}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
        </VisionCard>

        <VisionCard>
          <Heading size="md" color={sc.heading} mb={6} fontWeight="700">
            {t("quickActions")}
          </Heading>
          <Flex direction="column" gap={3}>
            <VisionButton
              variant="dark"
              w="100%"
              justifyContent="flex-start"
              h="auto"
              p={4}
              onClick={() => navigate({ to: "/tags" })}
            >
              <Box textAlign="left">
                <Text fontWeight="600" mb={1}>
                  {t("manageTags")}
                </Text>
                <Text fontSize="sm" color={sc.textMuted}>
                  {t("manageTagsDesc")}
                </Text>
              </Box>
            </VisionButton>
            <VisionButton
              variant="dark"
              w="100%"
              justifyContent="flex-start"
              h="auto"
              p={4}
              onClick={() => navigate({ to: "/readers" })}
            >
              <Box textAlign="left">
                <Text fontWeight="600" mb={1}>
                  {t("manageReaders")}
                </Text>
                <Text fontSize="sm" color={sc.textMuted}>
                  {t("manageReadersDesc")}
                </Text>
              </Box>
            </VisionButton>
          </Flex>
        </VisionCard>
      </Grid>
    </Box>
  )
}
